"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FunnelCreativesHeader,
  CreativeCard,
  CreativesGridSkeleton,
  CreateAdWithIAModal,
  ViewCreativeModal,
} from "@/feature/ads/cretives";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import type { CreativeItem, ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import { dictionary, Language } from "@/utils/dictionary";
import { truncateProductName } from "@/feature/ads/cretives/utils/truncateProductName";
import HyphensTabContent from "@/components/narratives/HyphensTabContent";
import useCreativeGenerationStore from "@/store/creativeGenerationStore";
import { GeneratingPlaceholderCard } from "@/feature/ads/cretives/components/GeneratingPlaceholderCard";
import { Video, Eye, Download, X, ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import { CreateVideoModal } from "@/feature/videos/components/CreateVideoModal";
import { ProductFunnelAngles } from "@/components/products/ProductFunnelAngles";
import { motion } from "framer-motion";
import { shareVideo } from "@/feature/videos/utils/shareVideo";

const isVideoStudioEnabled = import.meta.env.VITE_ENABLED_VIDEO === "true";

type AdsProductTab = "images" | "scripts" | "videos";

export default function AdsProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { language } = useAuth();

  const [activeTab, setActiveTab] = useState<AdsProductTab>("images");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewModalIndex, setViewModalIndex] = useState(0);
  const [viewModalEditMode, setViewModalEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CreativeItem[]>([]);
  const [product, setProduct] = useState<ProductInfoCreative | null>(null);

  // Video state
  const [videoItems, setVideoItems] = useState<CreativeItem[]>([]);
  const [videoLoading, setVideoLoading] = useState(true);
  const [viewVideoIndex, setViewVideoIndex] = useState(-1);
  const [isCreateVideoModalOpen, setIsCreateVideoModalOpen] = useState(false);
  const openCreateVideo = () => {
    setIsCreateVideoModalOpen(true);
  };

  const generatingItems = useCreativeGenerationStore((s) => s.items);
  const clearCompleted = useCreativeGenerationStore((s) => s.clearCompleted);
  const prevDoneIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedOnce = useRef(false);
  const hasLoadedVideosOnce = useRef(false);

  useEffect(() => {
    if (!productId) return;

    const loadProduct = async () => {
      try {
        const products = await creativesService.listProducts();
        const found = products.find((p) => p.product_id === productId) ?? null;
        setProduct(found);
      } catch (error) {
        console.error("Error loading product:", error);
      }
    };

    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (!productId || activeTab !== "images") return;

    const loadCreatives = async () => {
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      try {
        const data = await creativesService.listByProductId(productId);
        setItems(data.filter((item) => !!item.image_url));
      } catch (error) {
        console.error("Error loading creatives:", error);
        setItems([]);
      } finally {
        setLoading(false);
        hasLoadedOnce.current = true;
      }
    };

    loadCreatives();
  }, [productId, activeTab]);

  // Load video creatives
  useEffect(() => {
    if (!productId || activeTab !== "videos") return;

    const loadVideos = async () => {
      if (!hasLoadedVideosOnce.current) {
        setVideoLoading(true);
      }
      try {
        const data = await creativesService.listByProductId(productId);
        setVideoItems(data.filter((item) => item.creative_type === "VIDEO" && (!!item.video_url || !!item.base_image_url)));
      } catch (error) {
        console.error("Error loading video creatives:", error);
        setVideoItems([]);
      } finally {
        setVideoLoading(false);
        hasLoadedVideosOnce.current = true;
      }
    };

    loadVideos();
  }, [productId, activeTab]);

  // Re-fetch each time an individual item finishes, then clean up done items
  useEffect(() => {
    if (activeTab !== "images" || !productId) return;

    const doneItems = generatingItems.filter((i) => i.status === "done");
    const newlyDone = doneItems.filter((i) => !prevDoneIdsRef.current.has(i.id));

    if (newlyDone.length === 0) return;

    newlyDone.forEach((i) => prevDoneIdsRef.current.add(i.id));

    const reload = async () => {
      try {
        const data = await creativesService.listByProductId(productId);
        setItems(data.filter((item) => !!item.image_url));
      } catch (error) {
        console.error("Error reloading creatives:", error);
      }

      setTimeout(() => {
        clearCompleted();
        const remaining = useCreativeGenerationStore.getState().items;
        if (remaining.length === 0) {
          prevDoneIdsRef.current.clear();
        }
      }, 1500);
    };

    reload();
  }, [generatingItems, activeTab, productId, clearCompleted]);

  const handleViewCreative = (index: number) => {
    setViewModalIndex(index - generatingItems.length);
    setViewModalEditMode(false);
    setIsViewModalOpen(true);
  };

  const handleEditCreative = (index: number) => {
    setViewModalIndex(index - generatingItems.length);
    setViewModalEditMode(true);
    setIsViewModalOpen(true);
  };

  const renderCreativesContent = () => {
    if (loading && generatingItems.length === 0) {
      return <CreativesGridSkeleton />;
    }

    const showEmptyState = !loading && items.length === 0 && generatingItems.length === 0;

    if (showEmptyState) {
      return (
        <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <p className="text-gray-700 font-semibold">
            Este producto aún no tiene creativos.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Cuando generes creativos, aparecerán aquí.
          </p>
        </div>
      );
    }

    return (
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {generatingItems.map((item) => (
          <GeneratingPlaceholderCard key={item.id} item={item} />
        ))}
        {items.map((item, index) => (
          <CreativeCard
            key={item.id != null ? String(item.id) : item.image_url}
            item={item}
            index={index + generatingItems.length}
            onClick={() => {
              console.log("Creative clicked:", item.name || item.product_name);
            }}
            onView={handleViewCreative}
            onEdit={handleEditCreative}
          />
        ))}
      </div>
    );
  };

  const renderVideosContent = () => {
    if (videoLoading) {
      return <CreativesGridSkeleton />;
    }

    if (videoItems.length === 0) {
      return (
        <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-50 mb-4">
            <Video className="h-7 w-7 text-indigo-400" />
          </div>
          <p className="text-gray-700 font-semibold">
            {dictionary["no-videos-yet"]?.[language as Language] || "Este producto aún no tiene videos."}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            {dictionary["generate-videos-here"]?.[language as Language] || "Cuando generes videos, aparecerán aquí."}
          </p>
        </div>
      );
    }

    return (
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {videoItems.map((item, index) => (
          <VideoPreviewCard key={item.id} item={item} onView={() => setViewVideoIndex(index)} lang={language as Language} />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F6FA] font-['Plus_Jakarta_Sans']">
      {/* Tab switcher — fixed position */}
      <div className="max-w-[90%] mx-auto px-8 pt-8">
        <div className="flex items-center bg-gray-200 rounded-full p-1 w-fit mb-10">
        <button
          onClick={() => setActiveTab("images")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "images"
              ? "bg-white text-fluxi shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {dictionary["tab-images"]?.[language as Language] || "Imágenes"}
        </button>
        <button
          onClick={() => setActiveTab("scripts")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "scripts"
              ? "bg-white text-fluxi shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          {dictionary["tab-scripts"]?.[language as Language] || "Guiones"}
        </button>
        {isVideoStudioEnabled && (
          <button
            onClick={() => setActiveTab("videos")}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              activeTab === "videos"
                ? "bg-white text-fluxi shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {dictionary["tab-videos"]?.[language as Language] || "Videos"}
          </button>
        )}
        </div>
      </div>

      <div className="max-w-[90%] mx-auto px-8">
        <ProductFunnelAngles productId={productId} className="mb-8" />
      </div>

      {/* Images tab */}
      <div className={`max-w-[90%] mx-auto px-8 pb-8 ${activeTab !== "images" ? "hidden" : ""}`}>
        <FunnelCreativesHeader
          title={truncateProductName(product?.name?.trim() || "Producto")}
          imagesCount={items.length}
          thumbnailUrl={product?.image_url ?? items[0]?.image_url}
          onBack={() => navigate("/ads")}
          onCreate={() => setIsCreateModalOpen(true)}
          countLabel={dictionary["images"]?.[language as Language] || "imágenes"}
        />
        {renderCreativesContent()}

        <CreateAdWithIAModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          initialStep={2}
          initialProduct={product}
        />

        <ViewCreativeModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          creatives={items}
          initialIndex={viewModalIndex}
          language={language as Language}
          initialEditMode={viewModalEditMode}
          onCreativeUpdated={(id, newImageUrl) => {
            setItems((prev) =>
              prev.map((item) =>
                item.id === id ? { ...item, image_url: newImageUrl } : item
              )
            );
          }}
        />
      </div>

      {activeTab === "scripts" && <HyphensTabContent productId={productId} hideActions />}

      {/* Videos tab */}
      {activeTab === "videos" && (
        <div className="max-w-[90%] mx-auto px-8 pb-8">
          <FunnelCreativesHeader
            title={truncateProductName(product?.name?.trim() || "Producto")}
            imagesCount={videoItems.length}
            thumbnailUrl={product?.image_url ?? videoItems[0]?.base_image_url}
            onBack={() => navigate("/ads")}
            onCreate={openCreateVideo}
            createButtonLabel={dictionary["create-video-with-ai"]?.[language as Language] || "Crear Video con IA"}
            countLabel={dictionary["tab-videos"]?.[language as Language] || "Videos"}
          />
          {renderVideosContent()}

          <CreateVideoModal
            open={isCreateVideoModalOpen}
            onClose={() => setIsCreateVideoModalOpen(false)}
            initialProduct={product}
          />

          <ViewVideoModal
            videos={videoItems}
            initialIndex={viewVideoIndex}
            open={viewVideoIndex >= 0}
            onClose={() => setViewVideoIndex(-1)}
            lang={language as Language}
          />
        </div>
      )}
    </div>
  );
}

function VideoPreviewCard({ item, onView, lang }: { item: CreativeItem; onView: () => void; lang: Language }) {
  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!item.video_url) return;
    window.open(item.video_url, "_blank");
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    shareVideo({ url: item.video_url, title: item.product_name });
  };

  return (
    <div
      className="relative overflow-hidden rounded-xl bg-card border border-border/40 group cursor-pointer"
      onClick={onView}
    >
      <div className="relative w-full aspect-square overflow-hidden bg-gradient-to-b from-[#F8F8FF] to-white">
        {item.base_image_url ? (
          <img
            src={item.base_image_url}
            alt={item.product_name || dictionary["tab-videos"]?.[lang] || "Video"}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Video className="h-10 w-10 text-indigo-300" />
          </div>
        )}

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-150 flex items-center justify-center">
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
            <button
              onClick={(e) => { e.stopPropagation(); onView(); }}
              className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
              title={dictionary["view"]?.[lang] || "Ver"}
            >
              <Eye className="h-4 w-4" />
            </button>
            {item.video_url && (
              <>
                <button
                  onClick={handleShare}
                  className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
                  title="Compartir"
                >
                  <Share2 className="h-4 w-4" />
                </button>
                <button
                  onClick={handleDownload}
                  className="h-8 w-8 rounded-full flex items-center justify-center shadow-sm transition-colors bg-background/90 text-foreground hover:bg-background"
                  title={dictionary["download"]?.[lang] || "Descargar"}
                >
                  <Download className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-indigo-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold shadow-sm">
          <Video className="h-3 w-3" />
          {dictionary["tab-videos"]?.[lang] || "Video"}
        </div>
      </div>

      <div className="px-3 py-2.5 flex items-center gap-2 border-t border-border/30">
        {item.product_image_url && (
          <div className="h-5 w-5 rounded-full overflow-hidden flex-shrink-0 bg-muted/50 border border-border/30">
            <img src={item.product_image_url} alt="" className="h-full w-full object-cover" />
          </div>
        )}
        <span className="text-sm font-medium text-foreground truncate leading-tight">
          {item.product_name || dictionary["tab-videos"]?.[lang] || "Video"}
        </span>
      </div>
    </div>
  );
}

function ViewVideoModal({
  videos,
  initialIndex,
  open,
  onClose,
  lang,
}: {
  videos: CreativeItem[];
  initialIndex: number;
  open: boolean;
  onClose: () => void;
  lang: Language;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (open && initialIndex >= 0) setCurrentIndex(initialIndex);
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && currentIndex > 0) setCurrentIndex((i) => i - 1);
      if (e.key === "ArrowRight" && currentIndex < videos.length - 1) setCurrentIndex((i) => i + 1);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose, currentIndex, videos.length]);

  if (!open || videos.length === 0) return null;

  const safeIndex = Math.max(0, Math.min(currentIndex, videos.length - 1));
  const current = videos[safeIndex];
  const hasPrevious = safeIndex > 0;
  const hasNext = safeIndex < videos.length - 1;

  const handleDownload = () => {
    if (!current.video_url) return;
    window.open(current.video_url, "_blank");
  };

  const handleShare = () => {
    shareVideo({ url: current.video_url, title: current.product_name });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="relative z-10 flex flex-col items-center max-w-3xl w-full mx-4"
      >
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/70 hover:text-white transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="relative w-full flex items-center justify-center">
          {hasPrevious && (
            <button
              onClick={() => setCurrentIndex((i) => i - 1)}
              className="absolute -left-14 z-20 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          <div className="rounded-2xl overflow-hidden bg-black shadow-2xl max-h-[80vh] flex items-center justify-center">
            {current.video_url ? (
              <video
                key={current.video_url}
                src={current.video_url}
                controls
                autoPlay
                className="max-h-[80vh] w-auto"
              />
            ) : current.base_image_url ? (
              <img src={current.base_image_url} alt="" className="max-h-[80vh] w-auto" />
            ) : (
              <div className="h-96 w-64 flex items-center justify-center">
                <Video className="h-16 w-16 text-white/30" />
              </div>
            )}
          </div>

          {hasNext && (
            <button
              onClick={() => setCurrentIndex((i) => i + 1)}
              className="absolute -right-14 z-20 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-white/60 text-sm">
            {safeIndex + 1} / {videos.length}
          </span>
          {current.video_url && (
            <>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              >
                <Share2 className="h-4 w-4" />
                Compartir
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
                style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
              >
                <Download className="h-4 w-4" />
                {dictionary["download"]?.[lang] || "Descargar"}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
